const PI = Math.PI;

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

export const normalizeThetaDeg = (thetaDeg) => {
  const value = toFiniteNumber(thetaDeg);
  if (!Number.isFinite(value)) return Number.NaN;
  let normalized = value % 180;
  if (normalized < 0) normalized += 180;
  return normalized;
};

export const normalizePrincipalAngleDeg = (thetaDeg) => {
  const value = toFiniteNumber(thetaDeg);
  if (!Number.isFinite(value)) return Number.NaN;
  let normalized = value % 180;
  if (normalized < 0) normalized += 180;
  return normalized;
};

export const degToRad = (deg) => (deg * PI) / 180;
export const radToDeg = (rad) => (rad * 180) / PI;

export const computeMohrState = ({ sigmaX, sigmaY, tauXY, thetaDeg }) => {
  const sx = toFiniteNumber(sigmaX);
  const sy = toFiniteNumber(sigmaY);
  const txy = toFiniteNumber(tauXY);
  const theta = normalizeThetaDeg(thetaDeg);

  if (!Number.isFinite(sx) || !Number.isFinite(sy) || !Number.isFinite(txy) || !Number.isFinite(theta)) {
    return null;
  }

  const centerSigma = (sx + sy) / 2;
  const halfDeltaSigma = (sx - sy) / 2;
  const radius = Math.sqrt(halfDeltaSigma * halfDeltaSigma + txy * txy);

  const sigma1 = centerSigma + radius;
  const sigma3 = centerSigma - radius;
  const tauMax = radius;

  const thetaPrincipalRawDeg = 0.5 * radToDeg(Math.atan2(2 * txy, sx - sy));
  const thetaPrincipalDeg = normalizePrincipalAngleDeg(thetaPrincipalRawDeg);
  const thetaMaxShearDeg = normalizePrincipalAngleDeg(thetaPrincipalDeg + 45);

  const thetaRad = degToRad(theta);
  const cos2 = Math.cos(2 * thetaRad);
  const sin2 = Math.sin(2 * thetaRad);

  const sigmaTheta = centerSigma + halfDeltaSigma * cos2 + txy * sin2;
  const tauTheta = -halfDeltaSigma * sin2 + txy * cos2;

  return {
    centerSigma,
    radius,
    sigma1,
    sigma3,
    tauMax,
    thetaDeg: theta,
    thetaPrincipalDeg,
    thetaMaxShearDeg,
    points: {
      xFace: { sigma: sx, tau: txy },
      yFace: { sigma: sy, tau: -txy },
      selected: { thetaDeg: theta, sigma: sigmaTheta, tau: tauTheta },
      principal1: { sigma: sigma1, tau: 0 },
      principal3: { sigma: sigma3, tau: 0 },
      maxShearPositive: { sigma: centerSigma, tau: tauMax },
      maxShearNegative: { sigma: centerSigma, tau: -tauMax },
      // Backward-compatible aliases retained for existing render consumers.
      maxShearTop: { sigma: centerSigma, tau: tauMax },
      maxShearBottom: { sigma: centerSigma, tau: -tauMax },
    },
  };
};

export const computeEffectiveInputs = ({ sigmaX, sigmaY, tauXY, thetaDeg, porePressure }) => {
  const sx = toFiniteNumber(sigmaX);
  const sy = toFiniteNumber(sigmaY);
  const txy = toFiniteNumber(tauXY);
  const theta = toFiniteNumber(thetaDeg);
  const u = toFiniteNumber(porePressure);

  if (!Number.isFinite(sx) || !Number.isFinite(sy) || !Number.isFinite(txy) || !Number.isFinite(theta) || !Number.isFinite(u)) {
    return null;
  }

  return {
    sigmaX: sx - u,
    sigmaY: sy - u,
    tauXY: txy,
    thetaDeg: theta,
  };
};

export const formatValue = (value, digits = 3) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '-';
  return parsed.toFixed(digits);
};
